import React, { Children, useEffect, useRef } from 'react';

// Enums
import { BusinessUnit, BusinessUnitComponent } from '@zola-helpers/client/dist/es/@types';

// Utils
import { trackFlowStarted, trackFlowCompleted } from '@zola/tracking-contracts/src/tracking';
import {
  trackFlowCompleted as legacyTrackFlowCompleted,
  trackFlowStarted as legacyTrackFlowStarted,
} from '@zola-helpers/client/dist/es/tracking/flow/flowEvents';
import {
  identifyGuestUser,
  identifyUser,
} from '@zola-helpers/client/dist/es/redux/user/userActions';

// Components
import { XIcon } from '../../SvgIconsV3/X';
import AuthForms from '../../AuthForms';
import OnboardProgressBar from '../OnboardProgressBar';

// Context
import { ONBOARDING_PORTAL_CONTENT, useOnboardingContext } from '../context';

// hooks
import { usePrevious } from '../../../hooks/usePrevious';
import useEffectOnce from '../../../hooks/useEffectOnce';

// Styles
import {
  Container,
  ContentContainer,
  Content,
  SectionsContainer,
  SectionsContent,
  CloseOnboardingBtn,
  CloseOnboardingContainer,
} from './OnboardContainer.styles';

export type OnboardContainerProps = {
  businessUnit: BusinessUnit;
  businessUnitComponent: BusinessUnitComponent;
  className?: string;
  /** Optional function to fire when exiting flow prematurely */
  onClose?: () => void;
  /** What happens when the user successfully completes the entire onboard flow */
  onOnboardSuccessCb: () => Promise<void>;
  /** Text used for each section of flow. Length should correspond with number of sections in flow */
  progressTitles?: string[];
  /** Show/hide progress bar */
  showProgressBar?: boolean;
  /** Callback for when flow is opened; usually used to fire legacy Onboarding Started events */
  onOnboardingStarted?: () => void;
  /** An array of indexes indicating the end index for each progress bar title "grouping" */
  progressOverrides?: number[];
  /** How to handle a successful login from within the onboard flow.  Default is redirect to the homepage */
  onLoginSuccess?: () => Promise<void>;
};

const OnboardContainer: React.FC<OnboardContainerProps> = ({
  businessUnit,
  businessUnitComponent,
  className,
  children,
  progressTitles,
  showProgressBar = true,
  onClose = () => undefined,
  onOnboardSuccessCb,
  onOnboardingStarted,
  progressOverrides,
  onLoginSuccess,
}) => {
  const {
    onboardingState,
    setOnboardingState,
    toggleIsLogin,
    flowTrackingData,
    flowTrackingContractsData,
  } = useOnboardingContext();

  const { section, isLogin, initialUserContext } = onboardingState;
  const portalContainerRef = useRef<HTMLDivElement>(null);

  const allSections = Children.toArray(children);
  const activeSection = allSections[section];
  const sectionsLength = Children.count(children);

  useEffect(() => {
    if (portalContainerRef.current && !onboardingState.portalContainerRef) {
      setOnboardingState({ ...onboardingState, portalContainerRef });
    }
  }, [onboardingState, setOnboardingState]);

  useEffectOnce(() => {
    setOnboardingState({
      ...onboardingState,
      sectionsLength,
      business_unit_source: businessUnit,
      business_unit_feature: businessUnitComponent,
    });
  });

  useEffect(() => {
    if (initialUserContext.is_guest) {
      identifyGuestUser({ userContext: initialUserContext, hasGuestIdentified: false });
    } else {
      identifyUser({ userContext: initialUserContext, hasIdentified: false });
    }
  });

  /** Trigger any required "Onboarding Started" events in onOnboardingStarted  */
  useEffectOnce(() => {
    if (onOnboardingStarted) {
      onOnboardingStarted();
    }
    if (flowTrackingData) {
      legacyTrackFlowStarted(flowTrackingData);
    }
    trackFlowStarted(flowTrackingContractsData);
  });

  /** Perform actions after entire onboarding flow is completed */
  const prevCompleted = usePrevious(onboardingState.onboardingCompleted);
  useEffect(() => {
    const justCompleted = !prevCompleted && onboardingState.onboardingCompleted;
    if (justCompleted) {
      if (flowTrackingData) {
        legacyTrackFlowCompleted(flowTrackingData);
      }
      trackFlowCompleted(flowTrackingContractsData);
      onOnboardSuccessCb().catch(() => {
        // Uncaught callback error, redirect user to the homepage
        window.location.assign('/');
      });
    }
  }, [
    flowTrackingData,
    flowTrackingContractsData,
    onOnboardSuccessCb,
    onboardingState.onboardingCompleted,
    prevCompleted,
  ]);

  const loginCallback = () => {
    if (onLoginSuccess) {
      onLoginSuccess().catch(() => {
        // Some post login action went wrong... redirect to the homepage.
        window.location.assign('/');
      });
    } else {
      window.location.assign('/');
    }
  };

  return (
    <Container data-testid="OnboardContainer" className={className}>
      <CloseOnboardingContainer>
        <CloseOnboardingBtn onClick={onClose} data-testid="CloseOnboarding">
          <XIcon height={20} width={20} title="Close" />
        </CloseOnboardingBtn>
      </CloseOnboardingContainer>
      <ContentContainer>
        <Content id={ONBOARDING_PORTAL_CONTENT} ref={portalContainerRef} />
      </ContentContainer>
      <SectionsContainer>
        <SectionsContent isHidden={isLogin}>
          {sectionsLength > 1 && showProgressBar && (
            <OnboardProgressBar
              allSections={allSections}
              sectionIdx={section}
              progressTitles={progressTitles}
              progressOverrides={progressOverrides}
            />
          )}
          {activeSection}
        </SectionsContent>
        {isLogin && (
          <AuthForms
            loginCallback={loginCallback}
            signupLinkCallback={() => {
              toggleIsLogin();
              return false;
            }}
            useRefactoredRecaptcha
          />
        )}
      </SectionsContainer>
    </Container>
  );
};

export default OnboardContainer;
